import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trpc } from '../lib/trpc';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { Avatar } from '../components/ui/avatar';
import { useState } from 'react';
import {
  Bell,
  X,
  ChevronRight,
  ChevronDown,
  Clock,
  Users,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  XCircle,
  Wallet,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNicknames } from '../hooks/useNicknames';
import { REGULAR_CYCLE_DAYS } from '@so/shared';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Viewer's contribution and the period a regular-cycle notification refers to.
 * wave = cycle number the notification was created for; we derive dates from the
 * collection's current cycle so they stay correct even after later rollovers.
 */
function getCycleInfo(n: any, lang: string): { contribution: string | null; period: string } | null {
  if (n.type !== 'CYCLE_RENEWAL_REMINDER' && n.type !== 'CYCLE_RENEWED') return null;
  const c = n.collection;
  if (!c?.currentCycleStart || c.cycleNumber == null) return null;
  const current = new Date(c.currentCycleStart).getTime();
  // Reminder (wave = cycle in progress) points to the next cycle; renewed (wave = new cycle) to itself.
  const targetCycle = n.type === 'CYCLE_RENEWAL_REMINDER' ? (n.wave ?? c.cycleNumber) + 1 : (n.wave ?? c.cycleNumber);
  const start = new Date(current + (targetCycle - c.cycleNumber) * REGULAR_CYCLE_DAYS * DAY_MS);
  const end = new Date(start.getTime() + (REGULAR_CYCLE_DAYS - 1) * DAY_MS); // last day of the cycle
  const fmt = (d: Date) => d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });

  const obls = (c.obligations ?? []) as Array<{ amount: number }>;
  // Always in $: 1 $ = 1 unit of participation, no currency conversion (SO-40)
  const contribution = obls.length > 0
    ? `${Math.round(obls.reduce((s, o) => s + o.amount, 0) * 100) / 100} USD`
    : null;
  return { contribution, period: `${fmt(start)} — ${fmt(end)}` };
}

function getNotificationBadge(type: string, t: (key: string) => string): { label: string; variant: 'info' | 'warning' | 'default' | 'danger' | 'success' } {
  switch (type) {
    case 'NEW_COLLECTION': return { label: t('notifications.new'), variant: 'info' };
    case 'RE_NOTIFY': return { label: t('notifications.reNotify'), variant: 'warning' };
    case 'COLLECTION_CLOSED': return { label: t('notifications.closed'), variant: 'default' };
    case 'COLLECTION_BLOCKED': return { label: t('notifications.blocked'), variant: 'danger' };
    case 'OBLIGATION_RECEIVED': return { label: t('notifications.obligationReceived'), variant: 'success' };
    case 'CYCLE_CLOSED': return { label: t('notifications.cycleClosed'), variant: 'default' };
    case 'CYCLE_RENEWAL_REMINDER': return { label: t('notifications.renewalReminder'), variant: 'warning' };
    case 'CYCLE_RENEWED': return { label: t('notifications.renewalStart'), variant: 'info' };
    case 'SPECIAL_AUTHOR': return { label: t('notifications.specialAuthor'), variant: 'success' };
    case 'SPECIAL_DEVELOPER': return { label: t('notifications.specialDeveloper'), variant: 'success' };
    default: return { label: type, variant: 'default' };
  }
}

function timeRemaining(expiresAt: string | Date): string | null {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const resolve = useNicknames();
  const utils = trpc.useUtils();
  const [pendingIncomingCollapsed, setPendingIncomingCollapsed] = useState(false);
  const [pendingOutgoingCollapsed, setPendingOutgoingCollapsed] = useState(false);

  const { data, isLoading, fetchNextPage, hasNextPage } = (trpc.notification.list as any).useInfiniteQuery(
    { limit: 20 },
    { getNextPageParam: (last: { nextCursor?: string }) => last.nextCursor, refetchInterval: 30000 },
  );

  const markRead = trpc.notification.markRead.useMutation({ onSuccess: () => utils.notification.list.invalidate() });
  const dismiss = trpc.notification.dismiss.useMutation({ onSuccess: () => { utils.notification.list.invalidate(); utils.notification.unreadCount.invalidate(); } });
  const dismissAll = trpc.notification.dismissAll.useMutation({ onSuccess: () => { utils.notification.list.invalidate(); utils.notification.unreadCount.invalidate(); } });
  const { data: me } = trpc.user.me.useQuery();
  const unsubscribe = trpc.obligation.unsubscribeFromCollection.useMutation({
    onSuccess: () => {
      utils.notification.list.invalidate();
      utils.obligation.myList.invalidate();
    },
  });


  // Pending connections
  const { data: pendingIncoming } = trpc.pending.incoming.useQuery(undefined, { refetchInterval: 15000 });
  const { data: myPending } = trpc.pending.myPending.useQuery(undefined, { refetchInterval: 30000 });
  const [pendingError, setPendingError] = useState<string | null>(null);
  const refreshPending = () => {
    utils.pending.incoming.invalidate();
    utils.pending.incomingCount.invalidate();
    utils.connection.getNetworkStats.invalidate();
  };
  // Server error codes from connection-request.service → a clear next step
  const pendingErrorText = (code?: string) => {
    if (code === 'CONNECTION_LIMIT_SELF') return t('pending.errLimitSelf');
    if (code === 'CONNECTION_LIMIT_OTHER') return t('pending.errLimitOther');
    if (code === 'REQUESTER_DELETED') return t('pending.errRequesterGone');
    if (code === 'REQUEST_ALREADY_RESOLVED') return t('pending.errResolved');
    return t('common.error');
  };
  const acceptPending = trpc.pending.accept.useMutation({
    onMutate: () => setPendingError(null),
    onSuccess: refreshPending,
    onError: (err) => { setPendingError(pendingErrorText(err.message)); refreshPending(); },
  });
  const rejectPending = trpc.pending.reject.useMutation({
    onMutate: () => setPendingError(null),
    onSuccess: refreshPending,
    onError: (err) => { setPendingError(pendingErrorText(err.message)); refreshPending(); },
  });

  const notifications: any[] = data?.pages.flatMap((p: any) => p.items) ?? [];

  // Filter emergency notifications
  const emergencyNotifications = notifications.filter(
    (n: any) => n.type === 'NEW_COLLECTION' && n.status === 'UNREAD' && n.collection?.type === 'EMERGENCY' && n.collection?.status === 'ACTIVE'
  );

  // Group notifications by collectionId for stacked display
  type NotifItem = typeof notifications[0];
  type NotifGroup = { collectionId: string; items: NotifItem[] };
  const groupedNotifications: NotifGroup[] = [];
  const seenCollections = new Map<string, number>();
  for (const n of notifications) {
    const cId = n.collectionId;
    if (seenCollections.has(cId)) {
      groupedNotifications[seenCollections.get(cId)!]!.items.push(n);
    } else {
      seenCollections.set(cId, groupedNotifications.length);
      groupedNotifications.push({ collectionId: cId, items: [n] });
    }
  }

  const handleCardClick = (n: typeof notifications[0]) => {
    markRead.mutate({ id: n.id });
    // Don't navigate to collection if it's blocked — the user can't participate anymore
    if (n.type === 'COLLECTION_BLOCKED') return;
    // For the renewal reminder, navigation happens via the explicit "remind me" button instead
    if (n.type === 'CYCLE_RENEWAL_REMINDER') return;
    navigate(`/collection/${n.collectionId}`);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between pr-12">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Bell className="w-5 h-5" /> {t('notifications.title')}
        </h1>
        {notifications.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-gray-400"
            onClick={() => dismissAll.mutate()}
            disabled={dismissAll.isPending}
          >
            {t('notifications.dismissAll')}
          </Button>
        )}
      </div>

      {/* Emergency notifications */}
      {emergencyNotifications.length > 0 && (
        <Card className="border-red-500 dark:border-red-700 bg-red-50 dark:bg-red-950/30">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-700 dark:text-red-400">
                {t('dashboard.emergencyAlerts')} ({emergencyNotifications.length})
              </span>
            </div>
            <div className="space-y-2">
              {emergencyNotifications.slice(0, 3).map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => navigate(`/collection/${notif.collectionId}`)}
                  className="w-full text-left p-2 rounded bg-white/50 dark:bg-gray-900/50 hover:bg-white dark:hover:bg-gray-900"
                >
                  <div className="flex items-center gap-2">
                    <Avatar src={notif.collection?.creator?.photoUrl} name={notif.collection?.creator?.id ? resolve(notif.collection.creator.id, notif.collection.creator.name || '?') : (notif.collection?.creator?.name || '?')} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {notif.collection?.creator?.id ? resolve(notif.collection.creator.id, notif.collection.creator.name || '') : notif.collection?.creator?.name}
                        </p>
                        <span className="flex items-center gap-0.5 text-xs text-gray-400 shrink-0">
                          <Users className="w-3 h-3" />
                          {(notif.collection?.creator as any)?.connectionCount ?? 0}
                        </span>
                        <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400 shrink-0">
                          <Wallet className="w-3 h-3" />
                          ${Math.round((notif.collection?.creator as any)?.remainingBudget ?? 0)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-300">
                        {notif.collection?.amount} {notif.collection?.currency}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Collection notifications — grouped by collectionId with stacked overlap */}
      {isLoading ? <div className="flex justify-center py-12"><Spinner /></div> : groupedNotifications.length === 0 && (!pendingIncoming || pendingIncoming.length === 0) && (!myPending || myPending.length === 0) ? (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">{t('notifications.empty')}</div>
      ) : groupedNotifications.length > 0 ? (
        <div className="space-y-2">
          {groupedNotifications.map((group) => {
            const renderCard = (n: NotifItem, idx: number, isStacked: boolean) => {
              const resolvedPath = (n as any).handshakePathResolved as Array<{ id: string; name: string }> | undefined;
              const badge = getNotificationBadge(n.type, t);
              const remaining = n.expiresAt ? timeRemaining(n.expiresAt) : null;
              const creatorName = n.collection?.creator?.id
                ? resolve(n.collection.creator.id, n.collection.creator.name || '')
                : (n.collection?.creator?.name || '');
              const cycleInfo = getCycleInfo(n, i18n.language);
              const cycleInfoBlock = cycleInfo && (
                <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                  {cycleInfo.contribution && (
                    <p>{t('notifications.yourContribution')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{cycleInfo.contribution}</span></p>
                  )}
                  <p>{t('notifications.period')}: {cycleInfo.period}</p>
                </div>
              );

              // Renewal reminder card: explicit text + two action buttons
              if (n.type === 'CYCLE_RENEWAL_REMINDER') {
                return (
                  <Card
                    key={n.id}
                    className={cn(
                      'transition-colors relative',
                      n.status === 'UNREAD' && 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/20',
                    )}
                  >
                    <CardContent className="p-3 relative">
                      <button
                        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-300"
                        onClick={() => dismiss.mutate({ id: n.id })}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="flex items-center gap-2 mb-1.5 pr-6">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                      <div className="flex items-start gap-2.5 pr-6">
                        <Avatar src={n.collection?.creator?.photoUrl} name={creatorName} size="sm" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-700 dark:text-gray-200 leading-snug">
                            {t('notifications.renewalReminderBody', {
                              name: me?.name || '',
                              creator: creatorName,
                            })}
                          </p>
                          {cycleInfoBlock}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => { markRead.mutate({ id: n.id }); navigate(`/collection/${n.collectionId}`); }}
                        >
                          {t('notifications.renewalReminderAck')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                          disabled={unsubscribe.isPending}
                          onClick={() => unsubscribe.mutate({ collectionId: n.collectionId })}
                        >
                          {t('notifications.renewalReminderUnsubscribe')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              return (
                <Card
                  key={n.id}
                  className={cn(
                    'cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 relative',
                    n.status === 'UNREAD' && 'border-blue-500/50 bg-blue-50/50 dark:bg-blue-950/20',
                    isStacked && idx > 0 && 'bg-white dark:bg-gray-900',
                  )}
                  style={isStacked && idx > 0 ? { marginTop: -6, marginLeft: idx * 4, marginRight: idx * 4 } : undefined}
                  onClick={() => handleCardClick(n)}
                >
                  <CardContent className="p-3 relative">
                    <button
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-300 transition-colors"
                      onClick={(e) => { e.stopPropagation(); dismiss.mutate({ id: n.id }); }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-2 mb-1.5 pr-6">
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {remaining && (
                        <span className="text-xs text-orange-500 flex items-center gap-0.5">
                          <Clock className="w-3 h-3" /> {remaining}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2.5 pr-6">
                      <Avatar src={n.collection?.creator?.photoUrl} name={n.collection?.creator?.id ? resolve(n.collection.creator.id, n.collection.creator.name || '') : (n.collection?.creator?.name || '')} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{n.collection?.creator?.id ? resolve(n.collection.creator.id, n.collection.creator.name || '') : n.collection?.creator?.name}</span>
                          <span className="flex items-center gap-0.5 text-xs text-gray-400">
                            <Users className="w-3 h-3" />
                            {(n.collection?.creator as any)?.connectionCount ?? 0}
                          </span>
                          <span className="text-xs text-gray-400">·</span>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {n.collection?.amount != null ? `${n.collection.amount} ${n.collection.currency}` : n.collection?.currency}
                          </span>
                        </div>
                        {resolvedPath && resolvedPath.length > 0 && (
                          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-0.5 flex-wrap">
                            {t('notifications.via')}{' '}
                            {resolvedPath.map((user, i) => (
                              <span key={user.id} className="flex items-center gap-0.5">
                                {i > 0 && <ChevronRight className="w-3 h-3 shrink-0" />}
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/profile/${user.id}`); }}
                                  className="text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                                >
                                  {resolve(user.id, user.name)}
                                </button>
                              </span>
                            ))}
                            <span className="ml-1">({resolvedPath.length})</span>
                          </p>
                        )}
                        {cycleInfoBlock}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            };

            const isStacked = group.items.length > 1;

            return (
              <div key={group.collectionId}>
                {group.items.map((n, idx) => renderCard(n, idx, isStacked))}
              </div>
            );
          })}
          {hasNextPage && <Button variant="outline" className="w-full" onClick={() => fetchNextPage()}>{t('notifications.loadMore')}</Button>}
        </div>
      ) : null}

      {/* Incoming pending connections — collapsible, below collection notifications */}
      {pendingIncoming && pendingIncoming.length > 0 && (
        <Card className="border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="py-3">
            <button
              onClick={() => setPendingIncomingCollapsed(!pendingIncomingCollapsed)}
              className="flex items-center gap-2 w-full"
            >
              {pendingIncomingCollapsed ? <ChevronRight className="w-5 h-5 text-amber-600" /> : <ChevronDown className="w-5 h-5 text-amber-600" />}
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-700 dark:text-amber-400 flex-1 text-left">
                {t('pending.incoming')} ({pendingIncoming.length})
              </span>
            </button>
            {!pendingIncomingCollapsed && (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-300 mt-2 mb-2">{t('pending.meetInPerson')}</p>
                {pendingError && (
                  <p role="alert" className="text-sm text-red-600 dark:text-red-400 mb-2">{pendingError}</p>
                )}
                <div className="space-y-2">
                  {pendingIncoming.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 p-2 rounded bg-white/50 dark:bg-gray-900/50">
                      <button
                        onClick={() => navigate(`/profile/${p.fromUser.id}`)}
                        className="flex items-center gap-2 flex-1 min-w-0"
                      >
                        <Avatar src={p.fromUser.photoUrl} name={resolve(p.fromUser.id, p.fromUser.name)} size="sm" />
                        <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white truncate text-left">{resolve(p.fromUser.id, p.fromUser.name)}</span>
                      </button>
                      <button
                        onClick={() => acceptPending.mutate({ pendingId: p.id })}
                        disabled={acceptPending.isPending}
                        className="p-1.5 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 hover:bg-green-200 dark:hover:bg-green-800"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => rejectPending.mutate({ pendingId: p.id })}
                        disabled={rejectPending.isPending}
                        className="p-1.5 rounded-full bg-red-100 dark:bg-red-900/50 text-red-500 hover:bg-red-200 dark:hover:bg-red-800"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* My outgoing pending — collapsible */}
      {myPending && myPending.length > 0 && (
        <Card className="border-gray-200 dark:border-gray-700">
          <CardContent className="py-3">
            <button
              onClick={() => setPendingOutgoingCollapsed(!pendingOutgoingCollapsed)}
              className="flex items-center gap-2 w-full"
            >
              {pendingOutgoingCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-300 flex-1 text-left">{t('pending.waitingApproval')} ({myPending.length})</span>
            </button>
            {!pendingOutgoingCollapsed && (
              <div className="space-y-1 mt-2">
                {myPending.map((p) => (
                  <button key={p.id} className="flex items-center gap-2 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-1 -m-1 transition-colors" onClick={() => navigate(`/profile/${p.toUser.id}`)}>
                    <Avatar src={p.toUser.photoUrl} name={resolve(p.toUser.id, p.toUser.name)} size="xs" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{resolve(p.toUser.id, p.toUser.name)}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400 ml-auto" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
